import { msg } from "@lingui/core/macro";
import { For, Show } from "solid-js";
import { Palette, Plus } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@famidex/shared";
import { useI18n } from "@/providers/i18n-provider";
import { ThemeCard } from "./theme-card";
import { useCanWriteContent } from "../use-admin-capabilities";

type ThemeRow = components["schemas"]["ThemeDTO"];

export function ThemeManager() {
  const { t } = useI18n();
  const canWriteContent = useCanWriteContent();
  const themesQuery = createApiQuery<ThemeRow[], components["schemas"]["ErrorModel"]>(
    () => ["admin", "themes"],
    async () => api.GET("/api/admin/theme/listAll"),
    () => ({ staleTime: 30_000 }),
  );

  const toggleMutation = createApiMutation(async (vars: { themeId: string; isActive: boolean }) =>
    api.POST("/api/admin/theme/toggleActive", { body: vars }),
  );

  const deleteMutation = createApiMutation(async (vars: { themeId: string }) =>
    api.POST("/api/admin/theme/delete", { body: vars }),
  );

  const duplicateMutation = createApiMutation(async (vars: { themeId: string }) =>
    api.POST("/api/admin/theme/duplicate", { body: vars }),
  );

  const handleToggle = async (id: string, isActive: boolean) => {
    try {
      await toggleMutation.mutateAsync({ themeId: id, isActive });
      await themesQuery.refetch();
    } catch (err) {
      console.error("Error toggling theme:", err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync({ themeId: id });
      await themesQuery.refetch();
    } catch (err) {
      console.error("Error deleting theme:", err);
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      await duplicateMutation.mutateAsync({ themeId: id });
      await themesQuery.refetch();
    } catch (err) {
      console.error("Error duplicating theme:", err);
    }
  };

  return (
    <div class="space-y-8">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Palette class="h-8 w-8 text-primary" />
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Theme Management`)}</h2>
        </div>
        {/* Entrada de creación: se OCULTA (no hay nada que leer detrás). */}
        <Show when={canWriteContent()}>
          <Button class="gap-2">
            <Plus class="h-4 w-4" />
            {t(msg`New Theme`)}
          </Button>
        </Show>
      </div>

      <Show
        when={!themesQuery.isLoading}
        fallback={
          <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <For each={Array.from({ length: 6 }, () => 0)}>
              {() => <Skeleton class="h-48 w-full rounded-xl" />}
            </For>
          </div>
        }
      >
        <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <For each={themesQuery.data ?? []}>
            {(theme) => (
              <ThemeCard
                theme={theme}
                onToggle={handleToggle}
                onDelete={handleDelete}
                onDuplicate={handleDuplicate}
                isAnyPending={
                  toggleMutation.isPending ||
                  deleteMutation.isPending ||
                  duplicateMutation.isPending
                }
                t={t}
              />
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
